import { computed, unref, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import Contact from "@/services/contact/contact";

export interface ContactsParams {
  organizationId: string;
  searchTerm?: string;
  page?: number;
  limit?: number;
  createdById?: string;
  ownerId?: string | null;
  fname?: string;
  lname?: string;
  email?: string;
  mobileNumber?: string;
  jobTitle?: string;
  company?: string;
  industry?: string;
  bucketOptionId?: string;
  callDispositionId?: string;
  companyId?: string;
  tagId?: string;
  startDate?: string;
  endDate?: string;
  contactIds?: string;
  hasValidPhone?: boolean | null;
  hasValidEmail?: boolean | null;
  contactedVia?: string[];
  timezones?: string[];
  callDispositionIds?: string[];
  customFieldValues?: Record<string, any>;
}

function isRef<T>(param: Ref<T> | T): param is Ref<T> {
  return typeof param === "object" && param !== null && "value" in param;
}

export function useContacts(params: Ref<ContactsParams | null> | ContactsParams | null) {
  const queryKey = computed(() => {
    const p = isRef(params) ? params.value : params;
    if (!p) return ["contacts", "disabled"];
    return [
      "contacts",
      p.organizationId,
      p.searchTerm || "",
      p.page || 1,
      p.limit || 10,
      p.ownerId === undefined ? "all" : String(p.ownerId),
      p.createdById || "all",
      p.fname || "",
      p.lname || "",
      p.email || "",
      p.jobTitle || "",
      p.company || "",
      p.industry || "",
      p.bucketOptionId || "",
      p.callDispositionId || "",
      p.companyId || "",
      p.tagId || "",
      p.startDate || "",
      p.endDate || "",
      p.contactIds || "",
      p.hasValidPhone ?? "",
      p.hasValidEmail ?? "",
      (p.contactedVia || []).join(","),
      (p.timezones || []).join(","),
      (p.callDispositionIds || []).join(","),
      p.customFieldValues ? JSON.stringify(p.customFieldValues) : "",
    ];
  });

  const enabled = computed(() => {
    const p = isRef(params) ? params.value : params;
    return p !== null && p !== undefined && !!p.organizationId;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = isRef(params) ? params.value : params;
      if (!p) throw new Error("Parameters not ready");

      const queryParams: Record<string, any> = {
        organizationId: p.organizationId,
        page: p.page || 1,
        limit: p.limit || 25,
      };

      if (p.searchTerm) queryParams.searchTerm = p.searchTerm;
      if (p.ownerId !== undefined) queryParams.ownerId = p.ownerId;
      if (p.createdById) queryParams.createdById = p.createdById;
      if (p.fname) queryParams.fname = p.fname;
      if (p.lname) queryParams.lname = p.lname;
      if (p.email) queryParams.email = p.email;
      if (p.jobTitle) queryParams.jobTitle = p.jobTitle;
      if (p.company) queryParams.company = p.company;
      if (p.industry) queryParams.industry = p.industry;
      if (p.bucketOptionId) queryParams.bucketOptionId = p.bucketOptionId;
      if (p.callDispositionId) queryParams.callDispositionId = p.callDispositionId;
      if (p.companyId) queryParams.companyId = p.companyId;
      if (p.tagId) queryParams.tagId = p.tagId;
      if (p.startDate) queryParams.startDate = p.startDate;
      if (p.endDate) queryParams.endDate = p.endDate;
      if (p.contactIds) queryParams.contactIds = p.contactIds;
      if (p.mobileNumber) queryParams.mobileNumber = p.mobileNumber;
      if (p.hasValidPhone === true) queryParams.hasValidPhone = true;
      if (p.hasValidEmail === true) queryParams.hasValidEmail = true;
      if (p.contactedVia && p.contactedVia.length > 0) queryParams.contactedVia = p.contactedVia;
      if (p.timezones && p.timezones.length > 0) queryParams.timezones = p.timezones;
      if (p.callDispositionIds && p.callDispositionIds.length > 0)
        queryParams.callDispositionIds = p.callDispositionIds;
      if (p.customFieldValues && Object.keys(p.customFieldValues).length > 0) {
        queryParams.customFieldValues = JSON.stringify(p.customFieldValues);
      }

      const result = await Contact.getContacts(queryParams);

      if (result.data.status) {
        return {
          data: result.data.data,
          metaData: result.data.metaData,
        };
      }

      throw new Error(result.data.msg || "Failed to load contacts");
    },
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}
